import { VideoElement } from './VideoElement';

import type { ClipTranscriptSegment } from '../ClipTranscriptSegment';
import type { HasClipTranscriptSegment } from './HasClipTranscriptSegment';
import type {
  ActiveTranscriptTokenWithClipSegmentAttributes,
  ClipTranscriptToken,
} from '@src/editor/types';

export class VideoTranscriptElement
  extends VideoElement
  implements HasClipTranscriptSegment
{
  clipTranscriptSegment: ClipTranscriptSegment;

  constructor(
    props: Partial<VideoTranscriptElement> & {
      clipTranscriptSegment: ClipTranscriptSegment;
    },
  ) {
    super({ ...props, type: 'video' });

    this.clipTranscriptSegment = props.clipTranscriptSegment;
  }

  /**
   * Updates video element properties
   */
  override update(props: Partial<VideoTranscriptElement>): void {
    super.update(props);

    // Update transcript properties
    if (props.clipTranscriptSegment !== undefined) {
      this.clipTranscriptSegment = props.clipTranscriptSegment;
    }
  }

  /**
   * Captures the mutable state of the video element.
   */
  override getState(): Record<string, any> & {
    clipTranscriptSegment: ClipTranscriptSegment;
  } {
    return {
      ...super.getState(),
      clipTranscriptSegment: this.clipTranscriptSegment,
    };
  }

  /**
   * Restores the video element's mutable state.
   */
  override restoreState(state: Record<string, any>): void {
    super.restoreState(state);
    if (state.clipTranscriptSegment !== undefined)
      this.clipTranscriptSegment = state.clipTranscriptSegment;
  }

  /**
   * Creates a deep clone of this video element
   */
  override clone(): VideoTranscriptElement {
    const el = new VideoTranscriptElement({
      ...this.getState(),
    });

    return el;
  }

  getClipTranscriptSegment(): ClipTranscriptSegment {
    return this.clipTranscriptSegment;
  }

  getActiveTranscriptElements(): ActiveTranscriptTokenWithClipSegmentAttributes[] {
    const mapToTokenWithClipSegmentAttributes = (
      el: ClipTranscriptToken,
      idx: number,
    ): ActiveTranscriptTokenWithClipSegmentAttributes => {
      if (el.type === 'text') {
        return {
          ...el,
          ts: this.time + el.relative_ts,
          end_ts: this.time + el.relative_end_ts,
          clip_segment_id: this.clipTranscriptSegment.id,
          clip_segment_position: idx,
        };
      }
      return {
        ...el,
        clip_segment_id: this.clipTranscriptSegment.id,
        clip_segment_position: idx,
      };
    };

    return (
      this.clipTranscriptSegment
        .getWordsWithPunctuation()
        //.filter(el => el.state !== 'removed' && el.state !== 'cut') // punctuation can be removed inside
        .map(mapToTokenWithClipSegmentAttributes)
    );
  }

  getActiveTranscriptText(): string {
    const segment = this.getTranscriptSegment();
    if (!segment) {
      return '';
    }
    return segment.getText();
  }

  setClipTranscriptSegment(segment: ClipTranscriptSegment): void {
    this.clipTranscriptSegment = segment;
  }
}
